import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLS = [
  { v: 'pending', l: 'Pending', color: '#94a3b8' },
  { v: 'in_progress', l: 'In Progress', color: '#3b82f6' },
  { v: 'client_dependency', l: 'Client Dependency', color: '#f59e0b' },
  { v: 'blocked', l: 'Blocked', color: '#ef4444' },
  { v: 'completed', l: 'Completed', color: '#10b981' },
];
const PRIORITY_BADGE = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-rose-100 text-rose-700',
};
const emptyTask = () => ({
  project_id: '', title: '', description: '', assignee_employee_id: '',
  priority: 'medium', status: 'pending', due_date: '', estimated_hours: 0, subtasks: [],
});

export default function TaskBoardPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState({ project_id: '', assignee: '' });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTask());
  const isAdmin = user?.role === 'admin';
  const hasProjectsModule = (user?.extra_modules || []).includes('projects');
  const canManage = isAdmin || user?.is_team_lead || hasProjectsModule;
  const myEmpId = user?.employee_id || '';

  // Smart assignee list:
  // - Admin → all active employees
  // - Team Lead / projects-module user → if a project is picked, show only that project's `members` (+ self)
  //                                       otherwise show all active employees
  // - Regular staff → only themselves (self-assign)
  const assigneeOptions = useMemo(() => {
    const active = (employees || []).filter((e) => e.is_active !== false);
    if (isAdmin) return active;
    if (user?.is_team_lead || hasProjectsModule) {
      const proj = projects.find((p) => p.id === form.project_id);
      if (proj && Array.isArray(proj.members) && proj.members.length > 0) {
        const allowed = new Set([...(proj.members || []), myEmpId].filter(Boolean));
        const list = active.filter((e) => allowed.has(e.id));
        return list.length > 0 ? list : active;  // fallback to all if project has no members
      }
      return active;
    }
    // Plain staff → only themselves
    return active.filter((e) => e.id === myEmpId);
  }, [employees, projects, form.project_id, isAdmin, user?.is_team_lead, hasProjectsModule, myEmpId]);

  const load = async () => {
    const cfg = { withCredentials: true };
    const params = {};
    if (filter.project_id) params.project_id = filter.project_id;
    if (filter.assignee) params.assignee_employee_id = filter.assignee;
    const [tr, pr, er] = await Promise.all([
      axios.get(`${API_URL}/api/tasks`, { ...cfg, params }),
      axios.get(`${API_URL}/api/projects`, cfg).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/employees`, cfg).catch(() => ({ data: [] })),
    ]);
    setTasks(tr.data || []); setProjects(pr.data || []); setEmployees(er.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.project_id, filter.assignee]);

  const byStatus = useMemo(() => {
    const map = {};
    COLS.forEach((c) => { map[c.v] = []; });
    tasks.forEach((t) => { (map[t.status] || map.pending).push(t); });
    return map;
  }, [tasks]);

  const projName = (id) => projects.find((p) => p.id === id)?.name || '—';
  const empName = (id) => employees.find((e) => e.id === id)?.name || '—';

  const onDragStart = (e, taskId) => { e.dataTransfer.setData('task_id', taskId); };
  const onDrop = async (e, newStatus) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('task_id');
    if (!id) return;
    const t = tasks.find((x) => x.id === id);
    if (!t || t.status === newStatus) return;
    // Optimistic
    setTasks((prev) => prev.map((x) => (x.id === id ? { ...x, status: newStatus } : x)));
    try { await axios.put(`${API_URL}/api/tasks/${id}`, { status: newStatus }, { withCredentials: true }); }
    catch { load(); }
  };

  const submit = async () => {
    if (!form.title.trim() || !form.project_id) return alert('Project and title required');
    try {
      if (editing) await axios.put(`${API_URL}/api/tasks/${editing.id}`, form, { withCredentials: true });
      else         await axios.post(`${API_URL}/api/tasks`, form, { withCredentials: true });
      setShowForm(false); load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-3 p-6" data-testid="task-board-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-bold" style={{ color: '#1a2e4a' }}>Project Task Board</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <select className="border rounded px-2 py-1.5 text-xs bg-white" value={filter.project_id}
            onChange={(e) => setFilter({ ...filter, project_id: e.target.value })} data-testid="board-project-filter">
            <option value="">All projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {(isAdmin || user?.is_team_lead || hasProjectsModule) && (
            <select className="border rounded px-2 py-1.5 text-xs bg-white" value={filter.assignee}
              onChange={(e) => setFilter({ ...filter, assignee: e.target.value })}>
              <option value="">All assignees</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          )}
          {(canManage || myEmpId) && (
            <Button size="sm" onClick={() => {
              const init = { ...emptyTask(), project_id: filter.project_id || '' };
              // Regular staff (no projects module): pre-fill self-assignment
              if (!isAdmin && !user?.is_team_lead && !hasProjectsModule && myEmpId) init.assignee_employee_id = myEmpId;
              setEditing(null); setForm(init); setShowForm(true);
            }}
              data-testid="new-task-btn">
              <Plus size={14} className="mr-1" /> {(!isAdmin && !user?.is_team_lead && !hasProjectsModule) ? 'Add Self Task' : 'New Task'}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {COLS.map((c) => (
          <div key={c.v} className="bg-gray-50 rounded-lg p-2 min-h-[460px]"
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, c.v)}
            data-testid={`col-${c.v}`}>
            <div className="flex items-center justify-between px-1 pb-2 sticky top-0">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />
                <span className="text-xs font-bold uppercase tracking-wide" style={{ color: c.color }}>{c.l}</span>
              </div>
              <span className="text-[10px] text-gray-500 bg-white rounded-full px-2 py-0.5">{byStatus[c.v]?.length || 0}</span>
            </div>
            <div className="space-y-2">
              {byStatus[c.v]?.map((t) => (
                <Card key={t.id} draggable onDragStart={(e) => onDragStart(e, t.id)}
                  className="p-2.5 cursor-move hover:shadow-md bg-white"
                  onClick={() => { setEditing(t); setForm({ ...emptyTask(), ...t }); setShowForm(true); }}
                  data-testid={`task-card-${t.id}`}>
                  <div className="flex items-center gap-1 mb-1">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${PRIORITY_BADGE[t.priority] || ''}`}>
                      {(t.priority || 'medium').toUpperCase()}
                    </span>
                    {t.due_date && <span className="text-[10px] text-gray-500">📅 {t.due_date}</span>}
                  </div>
                  <div className="text-xs font-bold text-gray-900 line-clamp-2">{t.title}</div>
                  <div className="text-[10px] text-gray-500 mt-1 truncate">{projName(t.project_id)}</div>
                  <div className="flex items-center justify-between mt-1.5 text-[10px] text-gray-600">
                    <span>👤 {empName(t.assignee_employee_id)}</span>
                    {t.actual_hours > 0 && <span>⏱ {Number(t.actual_hours).toFixed(1)}h</span>}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowForm(false)}>
          <Card className="w-full max-w-xl max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-3" style={{ color: '#1a2e4a' }}>{editing ? 'Edit' : 'New'} Task</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" />
              </div>
              <div>
                <Label className="text-xs">Project *</Label>
                <select className="w-full border rounded px-3 py-2 text-sm bg-white" value={form.project_id}
                  onChange={(e) => setForm({ ...form, project_id: e.target.value })} data-testid="task-project">
                  <option value="">— Select —</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Assignee</Label>
                <select className="w-full border rounded px-3 py-2 text-sm bg-white" value={form.assignee_employee_id}
                  onChange={(e) => setForm({ ...form, assignee_employee_id: e.target.value })} data-testid="task-assignee">
                  <option value="">— None —</option>
                  {assigneeOptions.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}{e.emp_id ? ` (${e.emp_id})` : ''}{e.id === myEmpId ? ' — Me' : ''}
                    </option>
                  ))}
                </select>
                {!isAdmin && (
                  <p className="text-[11px] text-gray-500 mt-1">
                    {(user?.is_team_lead || hasProjectsModule)
                      ? (form.project_id
                          ? 'Showing project team members. To see more, add them to this project on the Projects page.'
                          : 'Pick a project above to filter to its team members.')
                      : 'You can only assign tasks to yourself. Ask a team lead to reassign.'}
                  </p>
                )}
                {assigneeOptions.length === 0 && (
                  <p className="text-[11px] text-amber-700 mt-1">No assignable employees found. Make sure your account is linked to an employee record.</p>
                )}
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <select className="w-full border rounded px-3 py-2 text-sm bg-white" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <select className="w-full border rounded px-3 py-2 text-sm bg-white" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {COLS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Due date</Label>
                <Input type="date" value={form.due_date || ''} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Estimated hours</Label>
                <Input type="text" inputMode="decimal" value={form.estimated_hours}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^0-9.]/g, '');
                    setForm({ ...form, estimated_hours: cleaned === '' ? 0 : parseFloat(cleaned) || 0 });
                  }} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Description</Label>
                <textarea className="w-full border rounded px-3 py-2 text-sm min-h-[70px]" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Subtasks (one per line — checkboxes will be created)</Label>
                <textarea className="w-full border rounded px-3 py-2 text-sm min-h-[70px] font-mono"
                  value={(form.subtasks || []).map((s) => (s.done ? '[x] ' : '[ ] ') + s.title).join('\n')}
                  onChange={(e) => {
                    const lines = e.target.value.split('\n').filter(Boolean);
                    const subs = lines.map((ln, i) => {
                      const m = /^\s*\[( |x|X)\]\s*(.*)$/.exec(ln);
                      return m ? { id: (form.subtasks?.[i]?.id) || `sub-${Date.now()}-${i}`, title: m[2], done: m[1].toLowerCase() === 'x' }
                               : { id: (form.subtasks?.[i]?.id) || `sub-${Date.now()}-${i}`, title: ln.trim(), done: false };
                    });
                    setForm({ ...form, subtasks: subs });
                  }} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              {editing && canManage && (
                <Button variant="outline" className="text-rose-700 border-rose-300"
                  onClick={async () => {
                    if (window.confirm('Delete this task and its updates?')) {
                      await axios.delete(`${API_URL}/api/tasks/${editing.id}`, { withCredentials: true });
                      setShowForm(false); load();
                    }
                  }}>Delete</Button>
              )}
              <Button onClick={submit} data-testid="save-task-btn">{editing ? 'Update' : 'Create'} Task</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
